import { useEffect, useState } from 'react';
import HomeHeader from '@/components/HomeHeader';
import Footer from '@/components/homepage/Footer';
import { usePageMeta } from '@/hooks/usePageMeta';
import { ChevronDown, ChevronUp, MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const FAQ_DESCRIPTION =
  "Frequently asked questions about Devotion Fund: how to raise support, how to give, fees, payouts, and more. For missionaries, volunteers, and supporters.";

interface FAQItem {
    question: string;
    answer: string;
    category: string;
}

const getFAQs = (t: (key: string) => string): FAQItem[] => [
    {
        category: 'General',
        question: t('faqsPage.questions.q1.q'),
        answer: t('faqsPage.questions.q1.a')
    },
    { category: 'General', question: t('faqsPage.questions.q2.q'), answer: t('faqsPage.questions.q2.a') },
    { category: 'General', question: t('faqsPage.questions.q3.q'), answer: t('faqsPage.questions.q3.a') },
    { category: 'General', question: t('faqsPage.questions.q4.q'), answer: t('faqsPage.questions.q4.a') },
    { category: 'For Fundraisers (Creators)', question: t('faqsPage.questions.q5.q'), answer: t('faqsPage.questions.q5.a') },
    { category: 'For Fundraisers (Creators)', question: t('faqsPage.questions.q6.q'), answer: t('faqsPage.questions.q6.a') },
    { category: 'For Fundraisers (Creators)', question: t('faqsPage.questions.q7.q'), answer: t('faqsPage.questions.q7.a') },
    { category: 'For Fundraisers (Creators)', question: t('faqsPage.questions.q8.q'), answer: t('faqsPage.questions.q8.a') },
    { category: 'For Fundraisers (Creators)', question: t('faqsPage.questions.q9.q'), answer: t('faqsPage.questions.q9.a') },
    { category: 'For Fundraisers (Creators)', question: t('faqsPage.questions.q10.q'), answer: t('faqsPage.questions.q10.a') },
    { category: 'For Fundraisers (Creators)', question: t('faqsPage.questions.q11.q'), answer: t('faqsPage.questions.q11.a') },
    { category: 'For Fundraisers (Creators)', question: t('faqsPage.questions.q12.q'), answer: t('faqsPage.questions.q12.a') },
    { category: 'For Supporters', question: t('faqsPage.questions.q13.q'), answer: t('faqsPage.questions.q13.a') },
    { category: 'For Supporters', question: t('faqsPage.questions.q14.q'), answer: t('faqsPage.questions.q14.a') },
    { category: 'For Supporters', question: t('faqsPage.questions.q15.q'), answer: t('faqsPage.questions.q15.a') },
    { category: 'For Supporters', question: t('faqsPage.questions.q16.q'), answer: t('faqsPage.questions.q16.a') },
    { category: 'Fees, Refunds & Transparency', question: t('faqsPage.questions.q17.q'), answer: t('faqsPage.questions.q17.a') },
    { category: 'Fees, Refunds & Transparency', question: t('faqsPage.questions.q18.q'), answer: t('faqsPage.questions.q18.a') },
    { category: 'Fees, Refunds & Transparency', question: t('faqsPage.questions.q19.q'), answer: t('faqsPage.questions.q19.a') },
    { category: 'Support & Troubleshooting', question: t('faqsPage.questions.q20.q'), answer: t('faqsPage.questions.q20.a') },
    { category: 'Support & Troubleshooting', question: t('faqsPage.questions.q21.q'), answer: t('faqsPage.questions.q21.a') },
    { category: 'Support & Troubleshooting', question: t('faqsPage.questions.q22.q'), answer: t('faqsPage.questions.q22.a') },
    { category: 'Coming Soon', question: t('faqsPage.questions.q23.q'), answer: t('faqsPage.questions.q23.a') }
];

function FAQSection({ question, answer, isOpen, onClick }: { question: string; answer: string; isOpen: boolean; onClick: () => void }) {
    return (
        <div className="border-b border-gray-200">
            <button
                onClick={onClick}
                className="w-full py-5 px-6 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
            >
                <h3 className="text-lg font-semibold text-gray-900 pr-8">{question}</h3>
                {isOpen ? (
                    <ChevronUp className="w-5 h-5 text-gray-500 flex-shrink-0" />
                ) : (
                    <ChevronDown className="w-5 h-5 text-gray-500 flex-shrink-0" />
                )}
            </button>
            {isOpen && (
                <div className="px-6 pb-5 text-gray-600 leading-relaxed">
                    <p>{answer}</p>
                </div>
            )}
        </div>
    );
}

export default function BestPractices() {
    usePageMeta('FAQs', FAQ_DESCRIPTION);
    const [openIndex, setOpenIndex] = useState<number | null>(null);
    const navigate = useNavigate();
    const { t } = useTranslation();

    const faqs = getFAQs(t);
    const categories = Array.from(new Set(faqs.map(faq => faq.category)));

    useEffect(() => {
        const jsonLd = {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: faqs.map((faq) => ({
                '@type': 'Question',
                name: faq.question,
                acceptedAnswer: { '@type': 'Answer', text: faq.answer },
            })),
        };
        const el = document.createElement('script');
        el.type = 'application/ld+json';
        el.setAttribute('data-best-practices-faq-jsonld', 'true');
        el.textContent = JSON.stringify(jsonLd);
        document.head.appendChild(el);
        return () => {
            el.remove();
        };
    }, [faqs]);

    const getCategoryTranslation = (category: string) => {
        const categoryMap: Record<string, string> = {
            'General': t('faqsPage.categories.general'),
            'For Fundraisers (Creators)': t('faqsPage.categories.forCreators'),
            'For Supporters': t('faqsPage.categories.forSupporters'),
            'Fees, Refunds & Transparency': t('faqsPage.categories.fees'),
            'Support & Troubleshooting': t('faqsPage.categories.support'),
            'Coming Soon': t('faqsPage.categories.comingSoon')
        };
        return categoryMap[category] || category;
    };

    return (
        <div className="min-h-screen bg-background flex flex-col">
            <HomeHeader />
            <main className="flex-grow pt-24 pb-16">
                <div className="max-w-4xl mx-auto px-6">
                    <div className="text-center mb-12">
                        <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
                            {t('faqsPage.hero.title')}
                        </h1>
                        <p className="text-xl text-muted-foreground">
                            {t('faqsPage.hero.subtitle')}
                        </p>
                    </div>

                    {/* Pricing summary */}
                    <section id="pricing" className="mb-12 bg-card rounded-2xl border border-border p-8 md:p-10 shadow-sm">
                        <div className="flex items-center gap-3 mb-4">
                            <span className="inline-block text-sm font-semibold text-primary uppercase tracking-wider">
                                {t('faqsPage.pricing.eyebrow')}
                            </span>
                        </div>
                        <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-6 leading-tight">
                            {t('faqsPage.pricing.title')}
                        </h2>
                        <div className="space-y-4 text-base md:text-lg text-muted-foreground leading-relaxed">
                            <p>{t('faqsPage.pricing.paragraph1')}</p>
                            <p>{t('faqsPage.pricing.paragraph2')}</p>
                            <p>{t('faqsPage.pricing.paragraph3')}</p>
                        </div>
                    </section>

                    {categories.map((category) => (
                        <div key={category} className="mb-10">
                            <div className="mb-4">
                                <h2 className="text-2xl font-bold text-gray-900 flex items-center">
                                    {category === 'For Fundraisers (Creators)' && <span className="mr-2">🙋</span>}
                                    {category === 'For Supporters' && <span className="mr-2">💝</span>}
                                    {category === 'Fees, Refunds & Transparency' && <span className="mr-2">💸</span>}
                                    {category === 'Support & Troubleshooting' && <span className="mr-2">⚙️</span>}
                                    {category === 'Coming Soon' && <span className="mr-2">🌍</span>}
                                    {getCategoryTranslation(category)}
                                </h2>
                            </div>
                            <div className="bg-white rounded-lg shadow-md overflow-hidden">
                                {faqs
                                    .filter(faq => faq.category === category)
                                    .map((faq) => {
                                        const globalIndex = faqs.indexOf(faq);
                                        return (
                                            <FAQSection
                                                key={globalIndex}
                                                question={faq.question}
                                                answer={faq.answer}
                                                isOpen={openIndex === globalIndex}
                                                onClick={() => setOpenIndex(openIndex === globalIndex ? null : globalIndex)}
                                            />
                                        );
                                    })}
                            </div>
                        </div>
                    ))}

                    <div className="mt-12 bg-gradient-to-br from-primary/5 to-secondary/10 rounded-3xl p-8 md:p-12">
                        <div className="bg-white rounded-3xl p-8 md:p-12 shadow-sm border border-border text-center">
                            <div className="w-16 h-16 bg-secondary/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
                                <MessageCircle className="w-8 h-8 text-secondary" />
                            </div>
                            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                                {t('faqsPage.cta.title')}
                            </h2>
                            <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
                                {t('faqsPage.cta.subtitle')}
                            </p>
                            <div className="flex flex-col sm:flex-row gap-4 justify-center">
                                <Button
                                    size="lg"
                                    className="bg-primary hover:bg-primary/90 text-primary-foreground text-lg px-8 py-6 rounded-full font-semibold"
                                    onClick={() => navigate('/contact')}
                                >
                                    {t('faqsPage.cta.contactButton')}
                                </Button>
                                <Button
                                    size="lg"
                                    className="bg-background hover:bg-background/90 text-foreground border-2 border-primary text-lg px-8 py-6 rounded-full font-semibold"
                                    onClick={() => navigate('/how-it-works')}
                                >
                                    {t('faqsPage.cta.howItWorksButton')}
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </main>
            <Footer />
        </div>
    );
}
